import { CircleAlert, CheckCircle2, Wallet } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorCard } from "@/components/shared/ErrorCard";
import { useSubscriptionStats } from "@/hooks/useSubscriptions";
import { formatCurrency } from "@/utils/format";

const monthLabel = (): string =>
  new Date().toLocaleString("en-GB", { month: "long", year: "numeric" });

interface RowProps {
  icon: typeof CircleAlert;
  iconClass: string;
  title: string;
  primary: string;
  secondary: string;
}

const Row = ({
  icon: Icon,
  iconClass,
  title,
  primary,
  secondary,
}: RowProps) => (
  <div className="flex items-start gap-3 py-3">
    <div
      className={`w-9 h-9 rounded-btn flex items-center justify-center shrink-0 ${iconClass}`}
    >
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-muted-foreground font-medium">{title}</p>
      <p className="text-lg font-bold text-foreground mt-0.5 leading-tight">
        {primary}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">{secondary}</p>
    </div>
  </div>
);

export const SubscriptionsSummary = () => {
  const { data, isLoading, error, refetch } = useSubscriptionStats();

  return (
    <div className="bg-card rounded-card border border-border p-5">
      <div className="mb-2">
        <h3 className="font-semibold text-foreground">Subscriptions</h3>
        <p className="text-sm text-muted-foreground mt-0.5">{monthLabel()}</p>
      </div>

      {isLoading ? (
        <div className="space-y-3 mt-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-md" />
          ))}
        </div>
      ) : error ? (
        <ErrorCard message={(error as Error).message} onRetry={refetch} />
      ) : (
        <div className="divide-y divide-border">
          <Row
            icon={CircleAlert}
            iconClass="bg-warning/10 text-warning"
            title="Pending collection"
            primary={formatCurrency(data?.pendingCollection.amount ?? 0)}
            secondary={`${data?.pendingCollection.count ?? 0} manual subscription${
              (data?.pendingCollection.count ?? 0) === 1 ? "" : "s"
            } outstanding`}
          />
          <Row
            icon={CheckCircle2}
            iconClass="bg-success/10 text-success"
            title="Collected this month"
            primary={formatCurrency(data?.collected.amount ?? 0)}
            secondary={`${data?.collected.count ?? 0} payment${
              (data?.collected.count ?? 0) === 1 ? "" : "s"
            } received`}
          />
          <Row
            icon={Wallet}
            iconClass="bg-primary-light text-primary-dark"
            title="Subscription revenue (all-time)"
            primary={formatCurrency(data?.totalRevenue ?? 0)}
            secondary={`${data?.transactionCount ?? 0} paid transactions`}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionsSummary;
